// SignaturePad.jsx
//
// Thin React wrapper around signature_pad for Written signatures.

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import SignaturePadLib from 'signature_pad';

// CSS
import './SignaturePad.css';

/**
 * @typedef {object} SignaturePadHandle
 * @property {() => boolean} isEmpty
 * @property {() => void} clear
 * @property {() => string|null} toDataURL
 */

/**
 * SignaturePad
 *
 * @param {object} props
 * @param {string} [props.penColor='#111111']
 * @param {string} [props.backgroundColor='rgba(0,0,0,0)']
 * @param {function} [props.onStrokeEnd]
 * @param {boolean} [props.disabled=false]
 */
const SignaturePad = forwardRef(({
  penColor = '#111111',
  backgroundColor = 'rgba(0,0,0,0)',
  onStrokeEnd = null,
  disabled = false,
}, ref) => {
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const onStrokeEndRef = useRef(onStrokeEnd);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    onStrokeEndRef.current = onStrokeEnd;
  }, [onStrokeEnd]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    let pad = null;
    let resizeObserver = null;

    const resizeCanvas = () => {
      if (!canvas || !padRef.current) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const width = canvas.offsetWidth || 320;
      const height = canvas.offsetHeight || 140;
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      const context = canvas.getContext('2d');
      if (context) {
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.scale(ratio, ratio);
      }
      padRef.current.clear();
      if (typeof onStrokeEndRef.current === 'function') onStrokeEndRef.current();
    };

    try {
      pad = new SignaturePadLib(canvas, {
        penColor,
        backgroundColor,
        minWidth: 0.8,
        maxWidth: 2.2,
      });
      padRef.current = pad;
      setFailed(false);

      const handleEnd = () => {
        if (typeof onStrokeEndRef.current === 'function') onStrokeEndRef.current();
      };
      pad.addEventListener('endStroke', handleEnd);

      resizeCanvas();
      resizeObserver = typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => resizeCanvas())
        : null;
      resizeObserver?.observe(canvas);
      window.addEventListener('resize', resizeCanvas);

      return () => {
        pad.removeEventListener('endStroke', handleEnd);
        window.removeEventListener('resize', resizeCanvas);
        resizeObserver?.disconnect();
        pad.off();
        padRef.current = null;
      };
    } catch (error) {
      console.warn('[SignaturePad] failed to initialize', error);
      setFailed(true);
      padRef.current = null;
      return undefined;
    }
  }, [backgroundColor, penColor]);

  useEffect(() => {
    const pad = padRef.current;
    if (!pad) return;
    if (disabled) pad.off();
    else pad.on();
  }, [disabled]);

  useImperativeHandle(ref, () => ({
    isEmpty: () => {
      if (failed || !padRef.current) return true;
      return padRef.current.isEmpty();
    },
    clear: () => {
      padRef.current?.clear();
    },
    toDataURL: (type = 'image/png') => {
      if (failed || !padRef.current || padRef.current.isEmpty()) return null;
      return padRef.current.toDataURL(type);
    },
    isAvailable: () => !failed && Boolean(padRef.current),
  }), [failed]);

  if (failed) {
    return (
      <div className="signature-pad is-failed">
        Drawing isn’t available on this device. Use a typed name instead.
      </div>
    );
  }

  return (
    <div className={`signature-pad${disabled ? ' is-disabled' : ''}`}>
      <canvas
        ref={canvasRef}
        className="signature-pad-canvas"
        aria-label="Signature drawing area"
      />
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
